import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useApi } from '../services/api'; // FIXED: Import useApi hook for authenticated API
import { supabase } from '../supabaseClient'; // Add this import to set session
import { Button, Card, Input, Label } from './ui/primitives';

const schema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  preferredName: z.string().min(1, 'Preferred name is required'),
  userType: z.custom<'EMPLOYER' | 'CANDIDATE'>((val) => {
    return val === 'EMPLOYER' || val === 'CANDIDATE';
  }, 'Invalid user type')
});

type FormData = z.infer<typeof schema>;

const SignupForm = () => {
  const { register, handleSubmit, formState: { errors }, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      userType: 'EMPLOYER',
    },
  });
  const api = useApi(); // FIXED: Use the hook to get authenticated api instance
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [legalError, setLegalError] = useState('');

  useEffect(() => {
    setValue('userType', 'EMPLOYER');
  }, [setValue]);

  const onSubmit = async (data: FormData) => {
    setErrorMessage('');
    setLegalError('');
    if (!acceptedLegal) {
      setLegalError('You must agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }

    try {
      const response = await api.post('/auth/signup', data);
      console.log('Signup success:', response.data);

      // Set session in frontend supabase client
      if (response.data.session) {
        await supabase.auth.setSession(response.data.session);
        console.log('Session set successfully');
        localStorage.setItem('token', response.data.session.access_token);
      } else {
        console.warn('No session in response—check backend');
      }

      if (data.userType === 'EMPLOYER') {
        navigate('/company-setup');
      } else {
        navigate('/applicant/dashboard');
      }
    } catch (error: any) {
      console.error('Signup error:', error.message || error);
      setErrorMessage(error.response?.data?.error || 'An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-surface-page flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md">
        <h2 className="text-[22px] md:text-page-title font-display font-bold text-txt-primary text-center">
          Sign Up
        </h2>
        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4">
          <div>
            <Label htmlFor="signup-email">Email</Label>
            <Input id="signup-email" {...register('email')} type="email" placeholder="you@company.com" autoComplete="username" />
            {errors.email && <p className="text-danger text-small mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <Label htmlFor="signup-password">Password</Label>
            <Input id="signup-password" {...register('password')} type="password" placeholder="Password" autoComplete="new-password" />
            {errors.password && <p className="text-danger text-small mt-1">{errors.password.message}</p>}
          </div>
          <div>
            <Label htmlFor="signup-name">Preferred Name</Label>
            <Input id="signup-name" {...register('preferredName')} type="text" placeholder="Preferred Name" autoComplete="name" />
            {errors.preferredName && <p className="text-danger text-small mt-1">{errors.preferredName.message}</p>}
          </div>
          <div>
            <Label htmlFor="signup-type">Profile Type</Label>
            <input type="hidden" {...register('userType')} value="EMPLOYER" />
            <Input id="signup-type" value="Employer" readOnly aria-readonly="true" />
            {errors.userType && <p className="text-danger text-small mt-1">{(errors.userType as { message: string }).message}</p>}
          </div>
          <label className="flex items-start gap-3 text-small text-txt-secondary">
            <input
              type="checkbox"
              checked={acceptedLegal}
              onChange={(event) => {
                setAcceptedLegal(event.target.checked);
                if (event.target.checked) {
                  setLegalError('');
                }
              }}
              className="mt-0.5 h-4 w-4 rounded border-line bg-surface-card text-brand-700 accent-brand-700 focus:ring-brand-700"
            />
            <span>
              I agree to the{' '}
              <a
                href="/legal/term-of-service.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 underline hover:text-brand-700"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="/legal/privacy-policy.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 underline hover:text-brand-700"
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
          {legalError && <p className="text-danger text-small">{legalError}</p>}
          {errorMessage && <p className="text-danger text-small">{errorMessage}</p>}
          <Button type="submit" fullWidth disabled={!acceptedLegal}>Sign Up</Button>
        </form>
      </Card>
    </div>
  );
};

export default SignupForm;
